/**
 * Main application component with routing setup.
 * 
 * PUBLIC ROUTES (no auth required):
 * - / - Public home page with recent essays
 * - /essays - Public essay listing
 * - /essays/:slug - Public essay detail with comments
 * - /category/:slug - Essays by category
 * - /tag/:slug - Essays by tag
 * - /categories - All categories listing
 * - /series/:slug - Series detail page
 * - /about - About page
 * - /login - Login page with Google SSO
 * 
 * REVIEWER PORTAL (auth required):
 * - /reviewer - Reviewer home page
 * - /reviewer/essays - Essay management
 * - /reviewer/essays/:slug - Essay editing/review
 * - /reviewer/admin - Admin panel (author only)
 * - /reviewer/search - Search page
 * 
 * Uses React Router for client-side navigation.
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from './hooks/useAuth';

// Layout components
import AppLayout from './components/AppLayout';
import PublicLayout from './components/PublicLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Auth pages
import LoginPage from './pages/LoginPage';
import EnterPage from './pages/EnterPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import BlockedPage from './pages/BlockedPage';

// Reviewer portal pages (protected)
import HomePage from './pages/HomePage';
import EssaysPage from './pages/EssaysPage';
import EssayDetailPage from './pages/EssayDetailPage';
import EssayCommentsPage from './pages/EssayCommentsPage';
import EssaySuggestionsPage from './pages/EssaySuggestionsPage';
import EssayHistoryPage from './pages/EssayHistoryPage';
import EssayInfoPage from './pages/EssayInfoPage';
import AdminPage from './pages/AdminPage';
import AnalyticsPage from './pages/AnalyticsPage';
import VisitorDetailPage from './pages/VisitorDetailPage';
import SearchResultsPage from './pages/SearchResultsPage';
import SeriesPage from './pages/SeriesPage';
import NotesLayout from './components/NotesLayout';
import NotesPage from './pages/NotesPage';
import NoteEditorPage from './pages/NoteEditorPage';

// Public pages (Phase 5)
import {
  PublicHomePage,
  PublicEssayListPage,
  PublicEssayPage,
  PublicCategoryPage,
  PublicTagPage,
  PublicCategoriesPage,
  PublicAboutPage,
  PublicSeriesPage,
} from './pages/public';

// Global styles
import './styles/global.css';

/**
 * App component - sets up routing and authentication context.
 */
function App() {
  const { isLoading } = useAuth();

  // Show loading spinner while checking authentication status
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#0a0a0f',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Enter page — standalone, no layout */}
        <Route path="/" element={<EnterPage />} />

        {/* ===== PUBLIC ROUTES ===== */}
        {/* These routes are accessible to everyone */}
        <Route element={<PublicLayout />}>
          <Route path="/home" element={<PublicHomePage />} />
          <Route path="/essays" element={<PublicEssayListPage />} />
          <Route path="/essays/:slug" element={<PublicEssayPage />} />
          <Route path="/category/:slug" element={<PublicCategoryPage />} />
          <Route path="/tag/:slug" element={<PublicTagPage />} />
          <Route path="/categories" element={<PublicCategoriesPage />} />
          <Route path="/series/:slug" element={<PublicSeriesPage />} />
          <Route path="/about" element={<PublicAboutPage />} />
        </Route>

        {/* Login page (public, no layout) */}
        <Route path="/login" element={<LoginPage />} />

        {/* Pending approval page (authenticated but not approved) */}
        <Route path="/pending-approval" element={<PendingApprovalPage />} />

        {/* Blocked page (authenticated but blocked) */}
        <Route path="/blocked" element={<BlockedPage />} />

        {/* ===== REVIEWER PORTAL (Protected) ===== */}
        {/* These routes require authentication */}
        <Route element={<ProtectedRoute />}>
          <Route path="/reviewer" element={<AppLayout />}>
            <Route index element={<HomePage />} />
            <Route path="essays" element={<EssaysPage />} />
            <Route path="essays/:slug" element={<EssayDetailPage />} />
            <Route path="essays/:slug/comments" element={<EssayCommentsPage />} />
            <Route path="essays/:slug/suggestions" element={<EssaySuggestionsPage />} />
            <Route path="essays/:slug/history" element={<EssayHistoryPage />} />
            <Route path="essays/:slug/info" element={<EssayInfoPage />} />
            {/* Admin page - author only (access control in component) */}
            <Route path="admin" element={<AdminPage />} />
            {/* Analytics page - author only */}
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="analytics/visitor/:visitorHash" element={<VisitorDetailPage />} />
            {/* Notes pages - author only, with sidebar layout */}
            <Route path="notes" element={<NotesLayout />}>
              <Route index element={<NotesPage />} />
              <Route path=":id" element={<NoteEditorPage />} />
            </Route>
            {/* Search page */}
            <Route path="search" element={<SearchResultsPage />} />
            {/* Series page in reviewer context */}
            <Route path="series/:slug" element={<SeriesPage />} />
          </Route>
        </Route>

        {/* Catch all - redirect to public home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
